import React from 'react';
import { useTranslation } from 'react-i18next';
import { FeedRegistrationsService } from './FeedRegistrationsService';
import { FeedDailyTotal } from '../models/feed-registration';
import useAuthContext from './useAuthContext';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseFeedDailyTotalsResult {
  totals: FeedDailyTotal[];
  loaded: boolean;
}

/**
 * Loads the registered feed per day and per feed behind a pen's chart.
 *
 * Kept apart from `useFeedRegistrations` because the table is paged and the chart must not be: a
 * chart drawn from one page would silently start wherever that page ended.
 */
export const useFeedDailyTotals = (
  locationIds: string[],
  startDate: string | undefined,
  endDate: string | undefined,
  reloadToken: number
): UseFeedDailyTotalsResult => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const [totals, setTotals] = React.useState<FeedDailyTotal[]>([]);
  const [loaded, setLoaded] = React.useState(false);

  const { farmId } = state;
  const locationKey = locationIds.join(',');

  React.useEffect(() => {
    if (!farmId || locationIds.length === 0) {
      setTotals([]);
      setLoaded(true);
      return;
    }

    let cancelled = false;

    new FeedRegistrationsService()
      .getDailyTotals(farmId, { locationIds, startDate, endDate })
      .then((result) => {
        if (cancelled) return;
        setTotals(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTotals([]);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, locationKey, startDate, endDate, reloadToken]);

  return { totals, loaded };
};

export default useFeedDailyTotals;
